<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :submitTitle="submitTitle"
    :scrollable="false"
    sizeClass="modal-xl"
    icon="fa-border-style-alt"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3" v-if="!loading">
      <FormRow label="Name">
        <FormInputText v-model="cutoutConfigData.name" field="name" placeholder="Name" />
      </FormRow>
      <FormRow label="Cutout Type">
        <FormSelect :options="OrderEditorCutoutTypeLookup" v-model="cutoutConfigData.type" field="type" />
      </FormRow>
      <FormRow label="Geometry Type" v-show="isCenterCutout">
        <FormSelect :options="geometryLookup" v-model="cutoutConfigData.geometryShapeType" field="geometryShapeType" />
      </FormRow>
      <FormHorizontalRule />
      <ContentTitle>Stock list</ContentTitle>

      <StaticDataTable class="static-table-list">
        <template v-slot:header>
          <tr>
            <th width="23%">Stock</th>
            <th width="15%"><Tooltip label="Description added to order items for cutouts. May be automatically appended by cutout size data" helpStyle>Description</Tooltip></th>
            <th colspan="2" width="30%"><Tooltip label="Stock filtered by the largest dimension of the cutout. Minimum is inclusive, maximum is exclusive" helpStyle>Dimensions</Tooltip></th>
            <th colspan="3" v-if="isCenterCutout && isGeometryCircle"><Tooltip label="Stock filtered by the countersunk setting on the cutout in the editor. Minimum is inclusive, maximum is exclusive" helpStyle>Countersunk</Tooltip></th>
            <th width="8%"><Tooltip label="Stock filtered by the polished setting on the cutout in the editor" helpStyle>Polished</Tooltip></th>
            <th width="5%" class="text-right">Actions</th>
          </tr>
        </template>
        <template v-slot:body>
          <tr v-for="cutoutStock in cutoutConfigData.orderEditorCutoutStocks" :key="cutoutStock.id">
            <td>
              <FormInputStockCode :stockType="InventoryStockTypes.glassService" v-model="cutoutStock.stockId" placeholder="Cutout Stock" :dropdownAutoWidth="true" />
            </td>
            <td>
              <FormInputText v-model="cutoutStock.description" placeholder="Description" />
            </td>
            <td>
              <FormInputText type="number" v-model="cutoutStock.dimensionFilterMin" placeholder="Minimum Value" prepend="Min" />
            </td>
            <td>
              <FormInputText type="number" v-model="cutoutStock.dimensionFilterMax" placeholder="Maximum Value" prepend="Max" />
            </td>
            <td v-if="isCenterCutout && isGeometryCircle">
              <FormCheckbox v-model="cutoutStock.hasCountersunkFilter" />
            </td>
            <td v-if="isCenterCutout && isGeometryCircle">
              <FormInputText v-show="cutoutStock.hasCountersunkFilter" type="number" :wholeNumber="true" v-model="cutoutStock.countersunkFilterMin" field="countersunkFilterMin" prepend="Min" />
            </td>
            <td v-if="isCenterCutout && isGeometryCircle">
              <FormInputText v-show="cutoutStock.hasCountersunkFilter" type="number" :wholeNumber="true" v-model="cutoutStock.countersunkFilterMax" field="countersunkFilterMax" prepend="Max" />
            </td>
            <td>
              <FormSelect :options="CutoutPolishFilterTypeLookup" v-model="cutoutStock.polishedFilterType" field="polishedFilterType" />        
            </td>
            <td class="text-right">
              <ActionDropMenu :tableAction="true">
                <DropdownMenuItem @click="removeCutoutStockItem(cutoutStock)" icon="fa-trash-alt">Remove</DropdownMenuItem>
              </ActionDropMenu>
            </td>
          </tr>
        </template>
        <template v-slot:empty v-if="cutoutConfigData.orderEditorCutoutStocks.length == 0">No stock items for the cutout found</template>
      </StaticDataTable>
      <button class="btn btn-white btn-xs" @click.prevent="addCutoutStockItem"><i class="far fa-plus"></i> Add Stock Item</button>
      
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'
import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import { InventoryStockTypes, OrderEditorCutoutTypes, OrderEditorCutoutTypeLookup, CutoutPolishFilterTypes, CutoutPolishFilterTypeLookup, OrderEditorGeometryShapeTypes, OrderEditorGeometryShapeTypeLookup } from '@/store/types'

export default {
  name: 'CutoutConfigDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
    FormInputStockCode
  },
  props: {
    isCreateMode: Boolean,
    id: Number,
  },
  data() {
    return {
      dialogForm: {},
      cutoutConfigData: {
        orderEditorCutoutStocks: []
      },
      submitting: false,
      loading: false,
      loadingError: '',

      // Geometry. Don't offer polygon for center cutout dropdowns
      geometryLookup: [
        { name: 'Circle', value: OrderEditorGeometryShapeTypes.circle },
        { name: 'Rectangle', value: OrderEditorGeometryShapeTypes.rectangle },
      ]
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.OrderEditorCutoutTypes = OrderEditorCutoutTypes
    this.OrderEditorCutoutTypeLookup = OrderEditorCutoutTypeLookup
    this.CutoutPolishFilterTypeLookup = CutoutPolishFilterTypeLookup
    this.OrderEditorGeometryShapeTypeLookup = OrderEditorGeometryShapeTypeLookup
  },
  mounted() {
    if(!this.isCreateMode && this.id) {
      this.loading = true
      this.loadingError = ''

      this.requestData()
      .then(() => {
      })
      .catch(err => {
        this.loadingError = 'Error loading data. Check your internet connection and try again'
      })
      .finally(() => {
        this.loading = false
      })
    }
  },
  computed: {
    title() {
      return this.isCreateMode ? 'New Cutout Item' : 'Edit Cutout Item'
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    isCenterCutout() {
      return this.cutoutConfigData.type == OrderEditorCutoutTypes.centerCutOut
    },
    isGeometryCircle() {
      return this.cutoutConfigData.geometryShapeType == OrderEditorGeometryShapeTypes.circle
    }
  },
  methods: {
    show() {
      // Reset form
      this.cutoutConfigData = {
        orderEditorCutoutStocks: []
      }
      this.dialogForm = {}
      this.submitting = false

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.submitting = true

      var updateData = _.cloneDeep(this.$data.cutoutConfigData)
      
      // Force non-center cutout to be polygons
      if(updateData.type != OrderEditorCutoutTypes.centerCutOut)
        updateData.geometryShapeType = OrderEditorGeometryShapeTypes.polygon      

      // Cleanup the cutout stock list
      updateData.orderEditorCutoutStocks = updateData.orderEditorCutoutStocks.map(stockItem => {
        var outputStock = _.cloneDeep(stockItem)
        if(outputStock.isNew)
          delete outputStock.id
        delete outputStock.isNew
        
        outputStock.hasCountersunkFilter = !!outputStock.hasCountersunkFilter

        if(!outputStock.hasCountersunkFilter) {
          delete outputStock.countersunkFilterMin
          delete outputStock.countersunkFilterMax
        }
          
        return outputStock
      })

      

      if(this.isCreateMode) {
        this.$apiService.orderEditorService.createOrderEditorCutout(updateData)
        .then(response => {
          this.submitting = false
          this.modalClosed(response.data.id)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.dialogForm = this.processFormResponse(err)
        })
      } else {
        this.$apiService.orderEditorService.updateOrderEditorCutout(updateData.id, updateData)
        .then(response => {
          this.submitting = false
          this.modalClosed(response.data.id)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.dialogForm = this.processFormResponse(err)
        })
      }
    },
    requestData() {
      return new Promise((resolve, reject) => {

        this.$apiService.orderEditorService.getOrderEditorCutout(this.id)
        .then(response => {
          this.cutoutConfigData = response.data

          return resolve()
        })
        .catch(err => {
          return reject(err)
        })
      })
    },
    removeCutoutStockItem(cutoutStockItem) {
      this.cutoutConfigData.orderEditorCutoutStocks = this.cutoutConfigData.orderEditorCutoutStocks.filter(item => item.id != cutoutStockItem.id)
    },
    addCutoutStockItem() {
      this.cutoutConfigData.orderEditorCutoutStocks.push({
        id: `temp-id-` + uuidv4(),
        isNew: true,
        stockId: null,
        dimensionFilterMin: 0,
        dimensionFilterMax: 0,
        polishedFilterType: CutoutPolishFilterTypes.any,
        hasCountersunkFilter: false,
        countersunkFilterMin: 0,
        countersunkFilterMax: 0
      })
    }
  }
}
</script>